import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Box, Typography} from '@mui/material';

interface ImageComparisonProps {
  leftImage: string;
  rightImage: string;
  leftLabel?: string;
  rightLabel?: string;
  fitMode?: 'contain' | 'cover';
}

export function ImageComparison({
  leftImage,
  rightImage,
  leftLabel = 'Before',
  rightLabel = 'After',
  fitMode = 'contain',
}: ImageComparisonProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftImageDimensions, setLeftImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [rightImageDimensions, setRightImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, percentage)));
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.touches[0].clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.min(100, Math.max(0, percentage)));
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchend', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isDragging]);

  // Load image dimensions
  useEffect(() => {
    const loadImageDimensions = (
      src: string,
      setSetter: (dims: {width: number; height: number}) => void
    ) => {
      const img = new Image();
      img.onload = () => {
        setSetter({width: img.naturalWidth, height: img.naturalHeight});
      };
      img.src = src;
    };

    loadImageDimensions(leftImage, setLeftImageDimensions);
    loadImageDimensions(rightImage, setRightImageDimensions);
  }, [leftImage, rightImage]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: isDragging ? 'ew-resize' : 'default',
        userSelect: 'none',
      }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
    >
      {/* Right Image (Bottom Layer) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `url(${rightImage})`,
          backgroundSize: fitMode,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Left Image (Top Layer with Clip) */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
          backgroundImage: `url(${leftImage})`,
          backgroundSize: fitMode,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Slider Line and Handle */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${sliderPosition}%`,
          transform: 'translateX(-50%)',
          width: '3px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          cursor: 'ew-resize',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
      >
        {/* Handle */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
          }}
        >
          ‹›
        </Box>
      </Box>

      {/* Labels */}
      {leftLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
          }}
        >
          <Typography sx={{fontSize: '14px', fontWeight: 'bold'}}>
            {leftLabel}
          </Typography>
          {leftImageDimensions && (
            <Typography sx={{fontSize: '12px', opacity: 0.8, mt: 0.5}}>
              {leftImageDimensions.width} × {leftImageDimensions.height}
            </Typography>
          )}
        </Box>
      )}
      {rightLabel && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
          }}
        >
          <Typography sx={{fontSize: '14px', fontWeight: 'bold'}}>
            {rightLabel}
          </Typography>
          {rightImageDimensions && (
            <Typography sx={{fontSize: '12px', opacity: 0.8, mt: 0.5}}>
              {rightImageDimensions.width} × {rightImageDimensions.height}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
